import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { DateRange } from '@/types/filters';

interface DateRangeFilterProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  placeholder?: string;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  placeholder = 'Select date range...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const formatDateRange = (range: DateRange): string => {
    if (!range.from && !range.to) {
      return placeholder;
    }

    if (range.from && range.to) {
      // Both dates selected - show range
      return `${format(range.from, 'dd.MM', { locale: de })} - ${format(range.to, 'dd.MM', { locale: de })}`;
    }

    if (range.from) {
      // Only from date selected - show single date with year and "ab" prefix
      return `ab ${format(range.from, 'dd.MM.yyyy', { locale: de })}`;
    }

    if (range.to) {
      // Only to date selected - show single date with year and "bis" prefix
      return `bis ${format(range.to, 'dd.MM.yyyy', { locale: de })}`;
    }

    return placeholder;
  };

  const handleRangeSelect = (range: DateRange | undefined) => {
    if (!range) {
      setTempRange({ from: undefined, to: undefined });
      return;
    }

    if (range.from && range.to) {
      // Complete range selected - use it directly
      setTempRange(range);
    } else if (range.from && !range.to) {
      // Only from date selected
      setTempRange((prev) => {
        const newRange = { ...prev, from: range.from };
        // If the new 'from' date is later than existing 'to', reset 'to'
        if (prev.to && range.from > prev.to) {
          newRange.to = undefined;
        }
        return newRange;
      });
    } else if (!range.from && range.to) {
      // Only to date selected
      setTempRange((prev) => {
        const newRange = { ...prev, to: range.to };
        // If the new 'to' date is earlier than existing 'from', reset 'from'
        if (prev.from && range.to < prev.from) {
          newRange.from = undefined;
        }
        return newRange;
      });
    }
  };

  const handleApply = () => {
    onChange(tempRange);
    setIsOpen(false);
  };

  const handleClear = () => {
    const clearedRange = { from: undefined, to: undefined };
    setTempRange(clearedRange);
    onChange(clearedRange);
    setIsOpen(false);
  };

  const handleClearFrom = () => {
    setTempRange((prev) => ({ ...prev, from: undefined }));
  };

  const handleClearTo = () => {
    setTempRange((prev) => ({ ...prev, to: undefined }));
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Initialize temp range with current value when opening
      setTempRange(value);
      // Default to 'from' mode when opening
    }
  };

  const hasValue = value.from || value.to;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="h-4 w-4 text-text-muted hover:text-text-primary cursor-pointer relative"
          title={formatDateRange(value)}
        >
          <CalendarIcon className="h-4 w-4 translate-y-0.5" />
          {hasValue && (
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
              •
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col border rounded-md overflow-hidden">
          {/* Calendar */}
          <Calendar
            mode="range"
            defaultMonth={tempRange.from || tempRange.to}
            selected={tempRange}
            onSelect={handleRangeSelect}
            numberOfMonths={1}
            locale={de}
            className="rounded-md border-0"
          />

          {/* Current Selection Display */}
          {(tempRange.from || tempRange.to) && (
            <div className="p-3 border-t bg-muted/30">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                Aktuelle Auswahl:
              </div>
              <div className="flex flex-col gap-2">
                {tempRange.from && (
                  <div className="flex-1 flex items-center justify-between p-2 bg-background rounded-md border">
                    <span className="text-sm font-medium text-muted-foreground">
                      Von:
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">
                        {format(tempRange.from, 'dd.MM.yyyy', { locale: de })}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearFrom}
                        className="h-4 w-4 p-0 hover:bg-gray-100"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
                {tempRange.to && (
                  <div className="flex-1 flex items-center justify-between p-2 bg-background rounded-md border">
                    <span className="text-sm font-medium text-muted-foreground">
                      Bis:
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">
                        {format(tempRange.to, 'dd.MM.yyyy', { locale: de })}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearTo}
                        className="h-4 w-4 p-0 hover:bg-gray-100"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="p-2 border-t flex justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-xs"
            >
              Löschen
            </Button>
            <Button size="sm" onClick={handleApply} className="text-xs">
              Anwenden
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
